'use client';

import classnames from 'classnames';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { handleTabListKeyDown, resolveTabStop } from './tab-list-keys';
import styles from './TabStrip.module.scss';

export interface TabDef<T extends string> {
    id: T;
    label: ReactNode;
    /** Shown but not selectable, e.g. a section that isn't available yet. */
    disabled?: boolean;
    /** The id of the panel this tab shows, for `aria-controls`. */
    controls?: string;
}

/**
 * - `equal`: every tab takes an equal share of the strip's width, and long
 *   labels are cut short with an ellipsis.
 * - `content`: each tab is as wide as its label, and the tabs sit centered.
 */
export type TabStripSizing = 'equal' | 'content';

const SIZING_CLASSES: Record<TabStripSizing, string | undefined> = {
    equal: undefined,
    content: styles.fitContent,
};

/** Any other prop (an `aria-label`, a `data-*` mark for a guided tour) goes on the tab list. */
interface TabStripProps<T extends string> extends Omit<ComponentPropsWithoutRef<'div'>, 'role' | 'onKeyDown' | 'children'> {
    tabs: TabDef<T>[];
    /** The selected tab, or `null` when the strip is shown off its own page and none is selected. */
    activeTab: T | null;
    onTabChange: (id: T) => void;
    /** Called with the tab under the pointer or keyboard focus, and `null` once it leaves. */
    onTabHover?: (id: T | null) => void;
    /** How the tabs share the strip's width. Defaults to `equal`. */
    sizing?: TabStripSizing;
}

/**
 * A row of framed tabs (the ARIA tabs pattern). Tab reaches the selected
 * tab; while a tab has focus the arrows, Home and End move between tabs, and
 * Enter or Space selects.
 */
export function TabStrip<T extends string>({
    tabs,
    activeTab,
    onTabChange,
    onTabHover,
    sizing = 'equal',
    className,
    ...listProps
}: TabStripProps<T>): JSX.Element {
    const tabStop = resolveTabStop(tabs, activeTab);
    return (
        <div
            role="tablist"
            className={classnames(styles.tabStrip, SIZING_CLASSES[sizing], className)}
            onKeyDown={handleTabListKeyDown}
            {...listProps}
        >
            {tabs.map((tab) => (
                <button
                    type="button"
                    role="tab"
                    key={tab.id}
                    aria-selected={activeTab === tab.id}
                    aria-controls={tab.controls}
                    tabIndex={tab.id === tabStop ? 0 : -1}
                    disabled={tab.disabled}
                    className={classnames(styles.tab, activeTab === tab.id && styles.active)}
                    onClick={() => onTabChange(tab.id)}
                    onPointerEnter={() => onTabHover?.(tab.id)}
                    onPointerLeave={() => onTabHover?.(null)}
                    onFocus={() => onTabHover?.(tab.id)}
                    onBlur={() => onTabHover?.(null)}
                >
                    <span className={styles.tabLabel}>{tab.label}</span>
                </button>
            ))}
        </div>
    );
}
